
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { KeyboardEvent, MouseEventHandler, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SinglePage from "../SinglePage/SinglePage";
import ProductImageCarousel from "./ProductImageCarousel";
import ShopNavbar from "./ShopNavbar";
import ShopHeroBannerImage from "../../../assets/images/shop/shop-herobanner.png";
import { getProductImagesWithFallback } from "./shopImageUtils";
import {
  CATEGORY_PAGE_LABELS,
  CATEGORY_TO_SHOP_SECTION,
  DEFAULT_CATEGORY,
  EXCLUDED_FROM_CLOTHING_TABS,
  HEXE_CATEGORY,
  HEXE_PRODUCT_SLUGS,
  KINDER_CATEGORY,
  KINDER_MATERIAL_CATEGORY,
  KINDER_MATERIAL_PRODUCT_SLUGS,
  KINDER_PRODUCT_SLUGS,
  SHOP_NAV_ITEMS,
  SHOP_SECTION_TO_CATEGORY,
  USED_CATEGORY,
} from "./shopNavigation";
import {
  createUsedShopProduct,
  deleteUsedShopProduct,
  expressInterestInUsedShopProduct,
  getShopCatalog,
  ShopProductDto,
  ShopProductOptionDto,
} from "../../../services/shopApi";
import { AUTH_CHANGED_EVENT, loadAuthSession, requestLoginDialog } from "../../../services/authStorage";
import ConfirmationDialog from "../../base/ConfirmationDialog";
import { COLORS } from "../../../values/colors";
import ShopActionFeedback from "./ShopActionFeedback/ShopActionFeedback";
import {
  AddModal,
  AddModalActions,
  AddModalCloseButton,
  AddModalHeader,
  AddModalOverlay,
  AddModalTitle,
  AddButton,
  CardFooter,
  CartHeader,
  CartItem,
  CartItemHeader,
  CartItemName,
  CartItemOptions,
  CartItemPrice,
  CartList,
  CartSlider,
  CartSummary,
  CategoryHeader,
  CategoryHeaderEyebrow,
  CategoryHeaderTitle,
  CheckoutButton,
  CheckoutHint,
  CloseCartButton,
  ContentContainer,
  DialogField,
  DialogFieldLabel,
  DialogInput,
  DialogIntroText,
  DialogPrimaryButton,
  DialogSecondaryButton,
  DialogTextArea,
  EmptyResult,
  EmptyText,
  FormGrid,
  HeroActionRow,
  HeroContent,
  HeroEyebrow,
  HeroPanel,
  HeroPrimaryAction,
  HeroSecondaryAction,
  HeroSection,
  HeroText,
  HeroTitle,
  OptionGroup,
  OptionLabel,
  OptionSelect,
  OptionTextInput,
  OptionValidationText,
  Overlay,
  ProductArea,
  ProductCard,
  ProductDescription,
  ProductGrid,
  ProductName,
  ProductPrice,
  QuantityButton,
  QuantityRow,
  QuantityValue,
  RemoveButton,
  ResultsText,
  SearchInput,
  SortSelect,
  SummaryRow,
  Toolbar,
  ToolbarField,
  ToolbarLabel,
  CartTitle,
} from "./styles";

interface CartEntry {
  key: string;
  productSlug: string;
  productName: string;
  price: number;
  quantity: number;
  selectedOptions: Record<string, string>;
}

interface CreateUsedProductForm {
  name: string;
  price: string;
  sourceName: string;
  size: string;
  comment: string;
}

type ShopSortMode = "default" | "price-asc" | "price-desc" | "name-asc" | "name-desc";

const CART_STORAGE_KEY = "shop-cart-entries";
const INITIAL_USED_PRODUCT_FORM: CreateUsedProductForm = { name: "", price: "", sourceName: "", size: "", comment: "" };

const formatPrice = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const formatOptions = (options: Record<string, string>): string => {
  const entries = Object.entries(options);
  return entries.length === 0 ? "Standard" : entries.map(([name, value]) => `${name}: ${value}`).join(" | ");
};

const buildCartKey = (productSlug: string, selectedOptions: Record<string, string>): string =>
  `${productSlug}|${Object.entries(selectedOptions)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([name, value]) => `${name}:${value}`)
    .join("|")}`;

const getProductImages = (product: ShopProductDto): string[] =>
  getProductImagesWithFallback(product.slug, product.imageUrls);

const isHexeProduct = (productSlug: string): boolean =>
  HEXE_PRODUCT_SLUGS.includes(productSlug as (typeof HEXE_PRODUCT_SLUGS)[number]);

const isKinderProduct = (productSlug: string): boolean =>
  KINDER_PRODUCT_SLUGS.includes(productSlug as (typeof KINDER_PRODUCT_SLUGS)[number]);

const isKinderMaterialProduct = (productSlug: string): boolean =>
  KINDER_MATERIAL_PRODUCT_SLUGS.includes(productSlug as (typeof KINDER_MATERIAL_PRODUCT_SLUGS)[number]);

const isExcludedFromClothingTabs = (productSlug: string): boolean =>
  EXCLUDED_FROM_CLOTHING_TABS.includes(productSlug as (typeof EXCLUDED_FROM_CLOTHING_TABS)[number]);

const sortProductsForStorefront = (items: ShopProductDto[], category: string | null = null): ShopProductDto[] => {
  const categoryOrder: readonly string[] | null =
    category === KINDER_CATEGORY
      ? KINDER_PRODUCT_SLUGS
      : category === KINDER_MATERIAL_CATEGORY
        ? KINDER_MATERIAL_PRODUCT_SLUGS
        : null;

  return [...items].sort((left, right) => {
    if (left.isSecondHandArticle !== right.isSecondHandArticle) {
      return left.isSecondHandArticle ? -1 : 1;
    }

    if (categoryOrder) {
      const leftIndex = categoryOrder.indexOf(left.slug);
      const rightIndex = categoryOrder.indexOf(right.slug);
      const normalizedLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
      const normalizedRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
      if (normalizedLeftIndex !== normalizedRightIndex) {
        return normalizedLeftIndex - normalizedRightIndex;
      }
    }

    if (left.isFeatured !== right.isFeatured) {
      return left.isFeatured ? -1 : 1;
    }

    if (left.displayOrder !== right.displayOrder) {
      return left.displayOrder - right.displayOrder;
    }

    return left.name.localeCompare(right.name, "de-DE");
  });
};

const sortProductsByMode = (items: ShopProductDto[], sortMode: ShopSortMode): ShopProductDto[] => {
  if (sortMode === "default") {
    return items;
  }

  return [...items].sort((left, right) => {
    if (sortMode === "price-asc") {
      return left.price - right.price || left.name.localeCompare(right.name, "de-DE");
    }

    if (sortMode === "price-desc") {
      return right.price - left.price || left.name.localeCompare(right.name, "de-DE");
    }

    if (sortMode === "name-desc") {
      return right.name.localeCompare(left.name, "de-DE");
    }

    return left.name.localeCompare(right.name, "de-DE");
  });
};

const matchesCategoryFilter = (product: ShopProductDto, category: string | null): boolean => {
  if (category === null) {
    return !product.isSecondHandArticle;
  }

  if (category === USED_CATEGORY) {
    return product.isSecondHandArticle;
  }

  if (product.isSecondHandArticle) {
    return false;
  }

  if (category === "Bekleidung Damen" || category === "Bekleidung Herren") {
    return product.category === category || (product.category === DEFAULT_CATEGORY && !isExcludedFromClothingTabs(product.slug));
  }

  if (category === HEXE_CATEGORY) {
    return isHexeProduct(product.slug);
  }

  if (category === KINDER_CATEGORY) {
    return !isKinderMaterialProduct(product.slug) && (product.category === KINDER_CATEGORY || isKinderProduct(product.slug));
  }

  if (category === KINDER_MATERIAL_CATEGORY) {
    return isKinderMaterialProduct(product.slug);
  }

  return product.category === category;
};

const buildDefaultSelection = (product: ShopProductDto): Record<string, string> =>
  product.options.reduce<Record<string, string>>((acc, option) => {
    if (option.values.length > 0) {
      acc[option.name] = option.values[0];
    }
    return acc;
  }, {});

const isStandaloneCustomOption = (option: ShopProductOptionDto): boolean =>
  option.allowsCustomValue && Boolean(option.customValueKey) && option.values.length === 0;

const buildSelectionWithCustomValues = (
  product: ShopProductDto,
  selection: Record<string, string>,
  customValues: Record<string, string>
) => {
  const normalizedSelection = { ...selection };

  product.options.forEach((option) => {
    if (!option.allowsCustomValue || !option.customValueKey) {
      return;
    }

    delete normalizedSelection[option.customValueKey];
    const shouldAttachCustomValue =
      (isStandaloneCustomOption(option) || selection[option.name] === option.customValueTrigger) &&
      Boolean(customValues[option.customValueKey]?.trim());

    if (shouldAttachCustomValue) {
      normalizedSelection[option.customValueKey] = customValues[option.customValueKey].trim();
    }
  });

  return normalizedSelection;
};

const validateCustomValues = (
  product: ShopProductDto,
  selection: Record<string, string>,
  customValues: Record<string, string>
) => {
  const errors: Record<string, string> = {};

  product.options.forEach((option) => {
    if (!option.allowsCustomValue || !option.customValueKey || !option.customValueLabel) {
      return;
    }

    if (!isStandaloneCustomOption(option) && selection[option.name] !== option.customValueTrigger) {
      return;
    }

    if (!customValues[option.customValueKey]?.trim()) {
      errors[option.customValueKey] = `Bitte ${option.customValueLabel.toLowerCase()} eingeben.`;
    }
  });

  return errors;
};
const OnlineShop = () => {
  const { shopSection } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(loadAuthSession());
  const [products, setProducts] = useState<ShopProductDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState<ShopSortMode>("default");
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Record<string, string>>>({});
  const [selectedCustomValues, setSelectedCustomValues] = useState<Record<string, Record<string, string>>>({});
  const [selectedAmounts, setSelectedAmounts] = useState<Record<string, number>>({});
  const [cartEntries, setCartEntries] = useState<CartEntry[]>(() => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? (parsed as CartEntry[]).map((entry) => ({
            ...entry,
            productSlug: entry.productSlug ?? entry.key.split("|")[0] ?? "",
          }))
        : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [modalProduct, setModalProduct] = useState<ShopProductDto | null>(null);
  const [modalSelection, setModalSelection] = useState<Record<string, string>>({});
  const [modalCustomValues, setModalCustomValues] = useState<Record<string, string>>({});
  const [modalCustomErrors, setModalCustomErrors] = useState<Record<string, string>>({});
  const [modalQuantity, setModalQuantity] = useState(1);
  const [actionMessage, setActionMessage] = useState("");
  const [actionTone, setActionTone] = useState<"error" | "success">("success");
  const [isUsedDialogOpen, setIsUsedDialogOpen] = useState(false);
  const [usedProductForm, setUsedProductForm] = useState<CreateUsedProductForm>(INITIAL_USED_PRODUCT_FORM);
  const [usedDialogError, setUsedDialogError] = useState("");
  const [isSavingUsedProduct, setIsSavingUsedProduct] = useState(false);
  const [interestProduct, setInterestProduct] = useState<ShopProductDto | null>(null);
  const [deleteProduct, setDeleteProduct] = useState<ShopProductDto | null>(null);
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const activeCategory = shopSection ? (SHOP_SECTION_TO_CATEGORY[shopSection] ?? null) : null;
  const activeCategoryLabel = activeCategory ? (CATEGORY_PAGE_LABELS[activeCategory] ?? activeCategory) : null;
  const token = session?.token ?? "";
  const isShopManager = session?.user.role === "shopmanager";

  useEffect(() => {
    const sync = () => setSession(loadAuthSession());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, sync);
  }, []);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);

    const loadCatalog = async () => {
      try {
        const catalog = await getShopCatalog(token || undefined);
        if (ignore) {
          return;
        }

        setProducts(catalog.products);
        setSelectedOptions(catalog.products.reduce<Record<string, Record<string, string>>>((acc, product) => {
          acc[product.slug] = buildDefaultSelection(product);
          return acc;
        }, {}));
        setSelectedCustomValues(catalog.products.reduce<Record<string, Record<string, string>>>((acc, product) => {
          acc[product.slug] = {};
          return acc;
        }, {}));
        setLoadError("");
      } catch (error) {
        if (!ignore) {
          setLoadError(error instanceof Error ? error.message : "Shop-Katalog konnte nicht geladen werden.");
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    void loadCatalog();
    return () => {
      ignore = true;
    };
  }, [token]);

  const featuredProducts = useMemo(() => {
    const prioritizedProducts = sortProductsForStorefront(products.filter((product) => !product.isSecondHandArticle));
    const explicitFeaturedProducts = prioritizedProducts.filter((product) => product.isFeatured);
    return explicitFeaturedProducts.length > 0 ? explicitFeaturedProducts : prioritizedProducts.slice(0, 8);
  }, [products]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.toLowerCase().trim();

    return products
      .filter((product) => matchesCategoryFilter(product, activeCategory))
      .filter((product) => {
        if (!normalizedSearch) {
          return true;
        }

        const searchIndex = [
          product.name,
          product.description,
          product.longDescription ?? "",
          product.category,
          product.sourceName ?? "",
          product.size ?? "",
          product.comment ?? "",
          ...product.articleNumbers.map((articleNumber) => `${articleNumber.code} ${articleNumber.label}`),
        ].join(" ").toLowerCase();

        return searchIndex.includes(normalizedSearch);
      });
  }, [activeCategory, products, searchTerm]);

  const isFeaturedMode = activeCategory === null && searchTerm.trim().length === 0;
  const visibleProducts = isFeaturedMode ? featuredProducts : filteredProducts;
  const sortedVisibleProducts = useMemo(
    () => sortProductsByMode(sortProductsForStorefront(visibleProducts, activeCategory), sortMode),
    [activeCategory, sortMode, visibleProducts]
  );
  const subtotal = useMemo(() => cartEntries.reduce((sum, item) => sum + item.price * item.quantity, 0), [cartEntries]);
  const cartItemsCount = useMemo(() => cartEntries.reduce((sum, entry) => sum + entry.quantity, 0), [cartEntries]);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartEntries));
  }, [cartEntries]);

  useEffect(() => {
    if (!actionMessage.trim()) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setActionMessage("");
    }, actionTone === "error" ? 6500 : 4200);

    return () => window.clearTimeout(timeout);
  }, [actionMessage, actionTone]);

  const setFeedback = (message: string, tone: "error" | "success") => {
    setActionMessage(message);
    setActionTone(tone);
  };

  const stopCardNavigation: MouseEventHandler<HTMLElement> = (event) => event.stopPropagation();

  const handleProductCardKeyDown = (event: KeyboardEvent<HTMLElement>, productSlug: string) => {
    if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      navigate(`/shop/produkt/${productSlug}`);
    }
  };

  const addToCart = (product: ShopProductDto, selection: Record<string, string>, customValues: Record<string, string>, quantityToAdd: number) => {
    const validationErrors = validateCustomValues(product, selection, customValues);
    if (Object.keys(validationErrors).length > 0) {
      setModalCustomErrors(validationErrors);
      return false;
    }

    const currentSelection = buildSelectionWithCustomValues(product, selection, customValues);
    const key = buildCartKey(product.slug, currentSelection);
    setCartEntries((prev) => {
      const existingItem = prev.find((entry) => entry.key === key);
      if (existingItem) {
        return prev.map((entry) => (entry.key === key ? { ...entry, quantity: entry.quantity + quantityToAdd } : entry));
      }

      return [...prev, { key, productSlug: product.slug, productName: product.name, price: product.price, quantity: quantityToAdd, selectedOptions: currentSelection }];
    });

    setSelectedOptions((prev) => ({ ...prev, [product.slug]: selection }));
    setSelectedCustomValues((prev) => ({ ...prev, [product.slug]: customValues }));
    setIsCartOpen(true);
    return true;
  };

  const removeEntry = (key: string) => setCartEntries((prev) => prev.filter((entry) => entry.key !== key));
  const updateCartQuantity = (key: string, direction: "up" | "down") => {
    setCartEntries((prev) => prev.flatMap((entry) => {
      if (entry.key !== key) {
        return [entry];
      }

      if (direction === "down" && entry.quantity === 1) {
        return [];
      }

      return [{ ...entry, quantity: direction === "up" ? entry.quantity + 1 : entry.quantity - 1 }];
    }));
  };
  const updateCardAmount = (productSlug: string, direction: "up" | "down") => {
    setSelectedAmounts((prev) => {
      const current = prev[productSlug] ?? 1;
      return { ...prev, [productSlug]: direction === "up" ? current + 1 : Math.max(1, current - 1) };
    });
  };
  const openAddModal = (product: ShopProductDto) => {
    if (!product.allowsDirectReservation) {
      return;
    }

    if (!loadAuthSession()) {
      requestLoginDialog();
      return;
    }

    const amount = selectedAmounts[product.slug] ?? 1;
    if (product.options.length === 0) {
      addToCart(product, {}, {}, amount);
      return;
    }

    setModalSelection(selectedOptions[product.slug] ?? buildDefaultSelection(product));
    setModalCustomValues(selectedCustomValues[product.slug] ?? {});
    setModalCustomErrors({});
    setModalQuantity(amount);
    setModalProduct(product);
  };

  const closeAddModal = () => {
    setModalProduct(null);
    setModalCustomErrors({});
  };

  const handleModalOptionChange = (option: ShopProductOptionDto, value: string) => {
    setModalSelection((prev) => ({ ...prev, [option.name]: value }));
    if (option.values.length > 0 && option.allowsCustomValue && option.customValueKey && value !== option.customValueTrigger) {
      setModalCustomValues((prev) => ({ ...prev, [option.customValueKey!]: "" }));
      setModalCustomErrors((prev) => {
        const next = { ...prev };
        delete next[option.customValueKey!];
        return next;
      });
    }
  };

  const confirmAddToCart = () => {
    if (modalProduct && addToCart(modalProduct, modalSelection, modalCustomValues, modalQuantity)) {
      closeAddModal();
    }
  };

  const openUsedDialog = () => {
    setUsedProductForm(INITIAL_USED_PRODUCT_FORM);
    setUsedDialogError("");
    setIsUsedDialogOpen(true);
  };

  const closeUsedDialog = () => {
    if (!isSavingUsedProduct) {
      setIsUsedDialogOpen(false);
      setUsedProductForm(INITIAL_USED_PRODUCT_FORM);
      setUsedDialogError("");
    }
  };

  const handleCreateUsedProduct = async () => {
    try {
      setIsSavingUsedProduct(true);
      const createdProduct = await createUsedShopProduct(token, {
        name: usedProductForm.name.trim(),
        price: Number(usedProductForm.price.replace(",", ".")),
        sourceName: usedProductForm.sourceName.trim(),
        size: usedProductForm.size.trim() || undefined,
        comment: usedProductForm.comment.trim() || undefined,
      });
      setProducts((prev) => [createdProduct, ...prev]);
      closeUsedDialog();
      setFeedback(`Gebrauchtartikel "${createdProduct.name}" wurde angelegt.`, "success");
    } catch (error) {
      setUsedDialogError(error instanceof Error ? error.message : "Gebrauchtartikel konnte nicht angelegt werden.");
    } finally {
      setIsSavingUsedProduct(false);
    }
  };

  const handleInterestClick = (product: ShopProductDto) => {
    if (!loadAuthSession()) {
      requestLoginDialog();
      return;
    }

    if (product.hasExpressedInterest) {
      setFeedback(`Für "${product.name}" wurde bereits Interesse bekundet.`, "error");
      return;
    }

    setInterestProduct(product);
  };

  const confirmInterest = async () => {
    if (!interestProduct) {
      return;
    }

    try {
      setIsSubmittingInterest(true);
      await expressInterestInUsedShopProduct(token, interestProduct.id);
      setProducts((prev) => prev.map((product) => (product.id === interestProduct.id ? { ...product, hasExpressedInterest: true } : product)));
      setFeedback(`Dein Interesse an "${interestProduct.name}" wurde versendet.`, "success");
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Interesse konnte nicht versendet werden.", "error");
    } finally {
      setIsSubmittingInterest(false);
      setInterestProduct(null);
    }
  };

  const confirmDeleteUsedProduct = async () => {
    if (!deleteProduct) {
      return;
    }

    try {
      setIsDeletingProduct(true);
      await deleteUsedShopProduct(token, deleteProduct.id);
      setProducts((prev) => prev.filter((product) => product.id !== deleteProduct.id));
      setFeedback(`Gebrauchtartikel "${deleteProduct.name}" wurde entfernt.`, "success");
      setDeleteProduct(null);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "Gebrauchtartikel konnte nicht entfernt werden.", "error");
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const scrollToProducts = () => document.getElementById("shop-products")?.scrollIntoView({ behavior: "smooth", block: "start" });
  const navigateToShopCategory = (category: string) => {
    const section = CATEGORY_TO_SHOP_SECTION.get(category);
    navigate(section ? (activeCategory === category ? "/shop" : `/shop/${section}`) : "/shop");
  };
  const goToCartReview = () => navigate("/shop/warenkorb");

  const usedProductPrice = Number(usedProductForm.price.replace(",", "."));
  const isCreateUsedProductDisabled =
    usedProductForm.name.trim().length === 0 ||
    usedProductForm.sourceName.trim().length === 0 ||
    !Number.isFinite(usedProductPrice) ||
    usedProductPrice <= 0;

  return (
    <>
      <SinglePage
        title={activeCategoryLabel ? `Shop ${activeCategoryLabel}` : "Online Shop"}
        heroBanner={!activeCategory ? (
          <HeroSection $backgroundImage={ShopHeroBannerImage}>
            <HeroPanel>
              <HeroContent>
                <HeroEyebrow>Offizieller Vereinsshop</HeroEyebrow>
                <HeroTitle>Kollektion der Eckenmoorhexen</HeroTitle>
                <HeroText>Bekleidung, Accessoires und besondere Artikel im Stil der Eckenmoorhexen Sulgen.</HeroText>
                <HeroActionRow>
                  <HeroPrimaryAction type="button" onClick={scrollToProducts}>Produkte entdecken</HeroPrimaryAction>
                  <HeroSecondaryAction type="button" onClick={() => navigate("/shop/hexe")}>Hexen-Kollektion ansehen</HeroSecondaryAction>
                </HeroActionRow>
              </HeroContent>
            </HeroPanel>
          </HeroSection>
        ) : <></>}
        content={
          <ContentContainer $hasHeroBanner={!activeCategory}>
            <ShopNavbar cartItemsCount={cartItemsCount} onCartClick={() => setIsCartOpen(true)} categories={SHOP_NAV_ITEMS} activeCategory={activeCategory} onCategorySelect={navigateToShopCategory} />
            {activeCategoryLabel ? (
              <CategoryHeader>
                <CategoryHeaderEyebrow>Shop Kategorie</CategoryHeaderEyebrow>
                <div style={{ display: "flex", width: "100%", gap: "1rem", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap" }}>
                  <CategoryHeaderTitle>{activeCategoryLabel}</CategoryHeaderTitle>
                  {activeCategory === USED_CATEGORY && isShopManager ? <AddButton type="button" onClick={openUsedDialog}>Artikel hinzufügen</AddButton> : null}
                </div>
                {activeCategory === USED_CATEGORY ? <ResultsText>{isShopManager ? "Shopmanager können hier Gebrauchtartikel anlegen und entfernen. Mitglieder können ihr Interesse per E-Mail an den Kleidleswart melden." : "Gebrauchtartikel können nicht direkt reserviert werden. Stattdessen kannst du dein Interesse bekunden."}</ResultsText> : null}
                <ShopActionFeedback message={actionMessage} tone={actionTone} />
              </CategoryHeader>
            ) : null}
            <ProductArea id="shop-products">
              <Toolbar>
                <ToolbarField>
                  <ToolbarLabel htmlFor="shop-search">Suche</ToolbarLabel>
                  <SearchInput id="shop-search" type="search" placeholder="Produkte suchen" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
                </ToolbarField>
                <ToolbarField>
                  <ToolbarLabel htmlFor="shop-sort">Sortieren nach</ToolbarLabel>
                  <SortSelect id="shop-sort" value={sortMode} onChange={(event) => setSortMode(event.target.value as ShopSortMode)}>
                    <option value="default">Standard</option>
                    <option value="price-asc">Preis aufsteigend</option>
                    <option value="price-desc">Preis absteigend</option>
                    <option value="name-asc">Alphabetisch A-Z</option>
                    <option value="name-desc">Alphabetisch Z-A</option>
                  </SortSelect>
                </ToolbarField>
              </Toolbar>
              <ResultsText>{isLoading ? "Produkte werden geladen" : loadError ? "Shop-Katalog konnte nicht geladen werden" : `${visibleProducts.length} Produkte gefunden`}</ResultsText>
              {loadError ? <EmptyResult>{loadError}</EmptyResult> : isLoading ? <EmptyResult>Bitte kurz warten.</EmptyResult> : visibleProducts.length === 0 ? <EmptyResult>{activeCategory === USED_CATEGORY ? "Aktuell sind keine gebrauchten Artikel verfügbar." : "Keine Produkte gefunden. Passe Suchtext oder Kategorie an."}</EmptyResult> : (
                <ProductGrid>
                  {sortedVisibleProducts.map((product) => (
                    <ProductCard key={product.slug} onClick={() => navigate(`/shop/produkt/${product.slug}`)} onKeyDown={(event) => handleProductCardKeyDown(event, product.slug)} role="button" tabIndex={0} aria-label={`${product.name} anzeigen`}>
                      <div onClick={stopCardNavigation}>
                        <ProductImageCarousel productSlug={product.slug} productName={product.name} imageUrls={getProductImages(product)} />
                      </div>
                      <ProductName>{product.name}</ProductName>
                      <ProductDescription>{product.isSecondHandArticle ? ([product.sourceName ? `Von ${product.sourceName}` : null, product.size ? `Größe ${product.size}` : null].filter(Boolean).join(" | ") || product.description) : product.description}</ProductDescription>
                      <ProductPrice>{formatPrice(product.price)}</ProductPrice>
                      <CardFooter>
                        {product.allowsDirectReservation ? (
                          <>
                            <QuantityRow>
                              <QuantityButton type="button" onClick={(event) => { event.stopPropagation(); updateCardAmount(product.slug, "down"); }}>-</QuantityButton>
                              <QuantityValue>{selectedAmounts[product.slug] ?? 1}</QuantityValue>
                              <QuantityButton type="button" onClick={(event) => { event.stopPropagation(); updateCardAmount(product.slug, "up"); }}>+</QuantityButton>
                            </QuantityRow>
                            <AddButton type="button" onClick={(event) => { event.stopPropagation(); openAddModal(product); }}>In den Warenkorb</AddButton>
                          </>
                        ) : product.isSecondHandArticle ? (
                          <>
                            {isShopManager ? <RemoveButton type="button" onClick={(event) => { event.stopPropagation(); setDeleteProduct(product); }}>Artikel löschen</RemoveButton> : null}
                            <AddButton type="button" disabled={product.hasExpressedInterest} onClick={(event) => { event.stopPropagation(); handleInterestClick(product); }}>{product.hasExpressedInterest ? "Interesse bereits bekundet" : "Interesse bekunden"}</AddButton>
                          </>
                        ) : null}
                      </CardFooter>
                    </ProductCard>
                  ))}
                </ProductGrid>
              )}
            </ProductArea>

            <Overlay type="button" $open={isCartOpen} onClick={() => setIsCartOpen(false)} aria-label="Warenkorb schließen" />
            <CartSlider $open={isCartOpen}>
              <CartHeader>
                <CartTitle>Warenkorb</CartTitle>
                <CloseCartButton type="button" onClick={() => setIsCartOpen(false)}>x</CloseCartButton>
              </CartHeader>
              {cartEntries.length === 0 ? <EmptyText>Dein Warenkorb ist aktuell leer.</EmptyText> : (
                <CartList>
                  {cartEntries.map((entry) => (
                    <CartItem key={entry.key}>
                      <CartItemHeader>
                        <CartItemName>{entry.productName}</CartItemName>
                        <CartItemPrice>{formatPrice(entry.price * entry.quantity)}</CartItemPrice>
                      </CartItemHeader>
                      <CartItemOptions>{formatOptions(entry.selectedOptions)}</CartItemOptions>
                      <QuantityRow>
                        <QuantityButton type="button" onClick={() => updateCartQuantity(entry.key, "down")}>-</QuantityButton>
                        <QuantityValue>{entry.quantity}</QuantityValue>
                        <QuantityButton type="button" onClick={() => updateCartQuantity(entry.key, "up")}>+</QuantityButton>
                        <RemoveButton type="button" onClick={() => removeEntry(entry.key)}>Entfernen</RemoveButton>
                      </QuantityRow>
                    </CartItem>
                  ))}
                </CartList>
              )}
              <CartSummary>
                <SummaryRow><span>Positionen</span><span>{cartItemsCount}</span></SummaryRow>
                <SummaryRow><strong>Gesamt</strong><strong>{formatPrice(subtotal)}</strong></SummaryRow>
                <CheckoutHint>Prüfe deinen Warenkorb auf einer eigenen Seite und sende danach eine unverbindliche Reservierung an den Shopmanager.</CheckoutHint>
                <CheckoutButton type="button" onClick={goToCartReview}>Zum Warenkorb</CheckoutButton>
              </CartSummary>
            </CartSlider>

      <AddModalOverlay type="button" $open={Boolean(modalProduct)} onClick={closeAddModal} aria-label="Produktoptionen schließen" />
      {modalProduct ? (
        <AddModal $open={Boolean(modalProduct)} role="dialog" aria-modal="true">
                <AddModalHeader>
                  <AddModalTitle>{modalProduct.name}</AddModalTitle>
                  <AddModalCloseButton type="button" onClick={closeAddModal}>x</AddModalCloseButton>
                </AddModalHeader>
                {modalProduct.options.map((option) => (
                  <OptionGroup key={`${modalProduct.slug}-${option.name}`}>
                    {option.values.length > 0 ? (
                      <>
                        <OptionLabel htmlFor={`${modalProduct.slug}-${option.name}`}>{option.name}</OptionLabel>
                        <OptionSelect id={`${modalProduct.slug}-${option.name}`} value={modalSelection[option.name] ?? option.values[0]} onChange={(event) => handleModalOptionChange(option, event.target.value)}>
                          {option.values.map((value) => <option key={value} value={value}>{value}</option>)}
                        </OptionSelect>
                      </>
                    ) : null}
                    {option.allowsCustomValue && option.customValueKey && (isStandaloneCustomOption(option) || modalSelection[option.name] === option.customValueTrigger) ? (
                      <OptionGroup>
                        <OptionLabel htmlFor={`${modalProduct.slug}-${option.customValueKey}`}>{option.customValueLabel}</OptionLabel>
                        <OptionTextInput
                          id={`${modalProduct.slug}-${option.customValueKey}`}
                          type="text"
                          maxLength={option.customValueMaxLength ?? 30}
                          value={modalCustomValues[option.customValueKey] ?? ""}
                          onChange={(event) => setModalCustomValues((prev) => ({ ...prev, [option.customValueKey!]: event.target.value }))}
                          placeholder={option.customValuePlaceholder ?? ""}
                        />
                        {modalCustomErrors[option.customValueKey] ? <OptionValidationText>{modalCustomErrors[option.customValueKey]}</OptionValidationText> : null}
                      </OptionGroup>
                    ) : null}
                  </OptionGroup>
                ))}
                <QuantityRow>
                  <QuantityButton type="button" onClick={() => setModalQuantity((prev) => Math.max(1, prev - 1))}>-</QuantityButton>
                  <QuantityValue>{modalQuantity}</QuantityValue>
                  <QuantityButton type="button" onClick={() => setModalQuantity((prev) => prev + 1)}>+</QuantityButton>
                </QuantityRow>
                <AddModalActions>
                  <AddButton type="button" onClick={confirmAddToCart}>In den Warenkorb</AddButton>
                </AddModalActions>
        </AddModal>
      ) : null}
          </ContentContainer>
        }
      />

      <Dialog
        open={isUsedDialogOpen}
        onClose={closeUsedDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "1.1rem",
            padding: "0.35rem",
            backgroundColor: COLORS.darkGray,
            color: COLORS.textPrimary,
            border: `1px solid ${COLORS.gray}`,
            boxShadow: "0 24px 60px rgba(0, 0, 0, 0.45)",
            backgroundImage: "none",
            opacity: 1,
          },
        }}
      >
        <DialogTitle sx={{ pb: 0, fontSize: "1.45rem", fontWeight: 700, color: COLORS.white }}>Gebrauchtartikel anlegen</DialogTitle>
        <DialogContent sx={{ pt: "1rem !important" }}>
          <DialogIntroText>
            Lege einen gebrauchten Artikel mit Preis, Herkunft und optionalem Kommentar an. Der Eintrag erscheint danach
            direkt in "Gebrauchte Artikel" und kann von Mitgliedern angefragt werden.
          </DialogIntroText>
          <FormGrid>
            <DialogField>
              <DialogFieldLabel>Artikelname</DialogFieldLabel>
              <DialogInput
                id="used-product-name"
                type="text"
                value={usedProductForm.name}
                onChange={(event) => setUsedProductForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="z. B. Hexenhäs Größe M"
                maxLength={120}
              />
            </DialogField>

            <DialogField>
              <DialogFieldLabel>Preis in EUR</DialogFieldLabel>
              <DialogInput
                id="used-product-price"
                type="text"
                inputMode="decimal"
                value={usedProductForm.price}
                onChange={(event) => setUsedProductForm((prev) => ({ ...prev, price: event.target.value }))}
                placeholder="z. B. 45,00"
              />
            </DialogField>

            <DialogField>
              <DialogFieldLabel>Von wem stammt der Artikel?</DialogFieldLabel>
              <DialogInput
                id="used-product-source"
                type="text"
                value={usedProductForm.sourceName}
                onChange={(event) => setUsedProductForm((prev) => ({ ...prev, sourceName: event.target.value }))}
                placeholder="Name oder Herkunft"
                maxLength={120}
              />
            </DialogField>

            <DialogField>
              <DialogFieldLabel>Größe</DialogFieldLabel>
              <DialogInput
                id="used-product-size"
                type="text"
                value={usedProductForm.size}
                onChange={(event) => setUsedProductForm((prev) => ({ ...prev, size: event.target.value }))}
                placeholder="z. B. M, 38 oder 152"
                maxLength={100}
              />
            </DialogField>

            <DialogField style={{ gridColumn: "1 / -1" }}>
              <DialogFieldLabel>Kommentar</DialogFieldLabel>
              <DialogTextArea
                id="used-product-comment"
                value={usedProductForm.comment}
                onChange={(event) => setUsedProductForm((prev) => ({ ...prev, comment: event.target.value }))}
                placeholder="Zustand, Besonderheiten oder Hinweise zum Artikel"
              />
            </DialogField>
          </FormGrid>
          {usedDialogError ? <OptionValidationText style={{ marginTop: "1rem" }}>{usedDialogError}</OptionValidationText> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1, justifyContent: "flex-end" }}>
          <DialogSecondaryButton type="button" onClick={closeUsedDialog}>Abbrechen</DialogSecondaryButton>
          <DialogPrimaryButton type="button" onClick={() => void handleCreateUsedProduct()} disabled={isCreateUsedProductDisabled || isSavingUsedProduct}>
            {isSavingUsedProduct ? "Speichern..." : "Artikel anlegen"}
          </DialogPrimaryButton>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog open={Boolean(interestProduct)} title="Interesse bekunden" description={interestProduct ? <>Du bekundest dein Interesse an <strong>{interestProduct.name}</strong>. Danach geht eine Bestätigung an dich und eine E-Mail an den Kleidleswart.</> : null} confirmText="Interesse senden" isConfirming={isSubmittingInterest} onConfirm={() => void confirmInterest()} onClose={() => setInterestProduct(null)} />
      <ConfirmationDialog open={Boolean(deleteProduct)} title="Gebrauchtartikel entfernen" description={deleteProduct ? <>Soll <strong>{deleteProduct.name}</strong> wirklich aus den Gebrauchtartikeln entfernt werden?</> : null} confirmText="Artikel entfernen" isConfirming={isDeletingProduct} onConfirm={() => void confirmDeleteUsedProduct()} onClose={() => setDeleteProduct(null)} />
    </>
  );
};

export default OnlineShop;
